/**
 * KeywordFilterDropdown - Keyword ability filter built on the generic FilterDropdown.
 *
 * Shared component used in both collection filter bar and deck editor toolbar.
 * Maps KEYWORD_ICON_MAP (all supported Scryfall keywords) into FilterDropdown options grouped as Evergreen/Other.
 */
import type { ReactElement } from 'react';

import type { FilterDropdownOption, FilterDropdownOptionGroup } from '@/components/FilterDropdown/FilterDropdown';
import FilterDropdown from '@/components/FilterDropdown/FilterDropdown';
import KeywordAbilityIcon from '@/components/KeywordIcons/KeywordAbilityIcon';
import { EVERGREEN_STYLE, KEYWORD_ICON_MAP, OTHER_STYLE, getCategoryStyle } from '@/lib/keywordIcons';

// ============================================================================
// Types
// ============================================================================

export interface KeywordFilterDropdownProps {
  selectedKeywords: string[];
  onToggle: (keyword: string) => void;
  onClear: () => void;
  className?: string;
}

// ============================================================================
// Static data (built once from KEYWORD_ICON_MAP)
// ============================================================================

const KEYWORD_GROUPS: FilterDropdownOptionGroup[] = [
  { groupName: 'Evergreen', style: { color: EVERGREEN_STYLE.color } },
  { groupName: 'Other', style: { color: OTHER_STYLE.color } },
];

const CATEGORY_TO_GROUP: Record<string, string> = {
  evergreen: 'Evergreen',
  other: 'Other',
};

const KEYWORD_OPTIONS: FilterDropdownOption[] = Object.entries(KEYWORD_ICON_MAP)
  .map(([keyword, def]) => {
    const style = getCategoryStyle(def.category);
    return {
      value: keyword,
      label: keyword
        .split(' ')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' '),
      icon: <KeywordAbilityIcon def={def} color={style.color} />,
      badgeClassName: style.pill,
      group: CATEGORY_TO_GROUP[def.category],
    };
  })
  .sort((a, b) => {
    const groupA = a.group ?? '';
    const groupB = b.group ?? '';
    if (groupA !== groupB) return groupA === 'Evergreen' ? -1 : 1;
    return a.value.localeCompare(b.value);
  });

const TRIGGER_ICON = <KeywordAbilityIcon def={KEYWORD_ICON_MAP.flying} size={14} />;

// ============================================================================
// Main Component
// ============================================================================

export default function KeywordFilterDropdown({
  selectedKeywords,
  onToggle,
  onClear,
  className,
}: KeywordFilterDropdownProps): ReactElement {
  return (
    <FilterDropdown
      triggerLabel="Keywords"
      triggerIcon={TRIGGER_ICON}
      ariaLabel="Keyword filter"
      searchPlaceholder="Search keywords..."
      groups={KEYWORD_GROUPS}
      options={KEYWORD_OPTIONS}
      selectedValues={selectedKeywords}
      onToggle={onToggle}
      onClear={onClear}
      className={className}
    />
  );
}
